const en = {
  title: 'Info & Layout',
  description: "Your festival's name, its lineup release, and how the app looks.",
  sectionsLabel: 'Sections',
  sections: {
    festival: 'Festival',
    theme: 'Theme',
    navigation: 'Navigation',
    surfaces: 'Surfaces',
    screens: 'Screens',
    images: 'Header images',
  },
  festival: {
    name: 'Festival name',
    nameHelp: 'The official name of the festival, shown in the app.',
    internalName: 'Internal name',
    internalNameHelp: 'Names this version in the backoffice only. Leave empty to use the festival name.',
  },
  lineup: {
    title: 'Lineup release',
    available: 'Lineup publicly available',
    releaseDate: 'Release date',
    releaseDatePast: 'Release date is in the past — lineup is visible.',
    help: 'When off, the app hides the lineup until the release date.',
  },
  homepage: {
    title: 'Homepage',
    enabled: 'Open the app on the homepage',
    help: 'Adds a homepage with live shows, team recommendations, news and useful shortcuts. The lineup remains available in its own tab.',
    startDate: 'Festival start',
    endDate: 'Festival end',
    datesHelp: 'Optional: otherwise the first and last shows determine the dates. Include the final sets after midnight. These dates switch between before, during and after the festival.',
    beforeMessage: 'Message before the festival',
    afterMessage: 'Message after the festival',
    messagesHelp: 'Leave empty to use the app’s default welcome and thank-you messages.',
  },
  reminders: {
    title: 'Show reminders',
    minutes: 'Reminder before a show (minutes)',
    placeholder: (minutes: number) => `${minutes} (app default)`,
    help: (minutes: number) =>
      `How long before a followed show starts the app sends its reminder. Leave empty to use the app default (${minutes} min). Users can still change it in the app.`,
  },
  /** Under a 0–1 opacity field */
  opacityHelp: 'Value between 0.0 (transparent) and 1.0 (opaque)',
  brand: {
    title: 'Brand colors',
    accentColor: 'Accent color',
    accentColorHelp: 'Buttons, switches, loading spinners, badges, active filters and the timetable.',
    accentColorAutomatic:
      "Buttons, switches, loading spinners, badges, active filters and the timetable. Automatic: follows the tab bar's selected color.",
    onAccentColor: 'Text on the accent',
    onAccentColorHelp: 'Text and icons on the accent color, like button labels.',
    onAccentColorAutomatic: 'Text and icons on the accent color, like button labels. Automatic: black or white, whichever reads best.',
    backToAutomatic: 'Back to automatic',
    linkColor: 'Link color',
    linkColorHelp: 'Links in news, pages and the tickets screen.',
    sampleButton: 'Button',
    sampleLink: 'Link',
  },
  text: {
    title: 'Text on the background',
    help: 'Text drawn straight on the festival background: the splash screen title, day separators and empty screens.',
    titleColor: 'Title color',
    captionColor: 'Caption color',
    bodyColor: 'Body text color',
  },
  background: {
    title: 'Background',
    type: 'Background type',
    types: {
      color: 'Solid color',
      gradient: 'Gradient',
      image: 'Image',
    },
    primaryColor: 'Primary color',
    secondaryColor: 'Secondary color',
    image: 'Background image',
    imageOpacity: 'Image opacity',
    baseColor: 'Color under the image',
    baseColorHelp: 'Shows through the image as its opacity goes down.',
  },
  shapes: {
    title: 'Shapes',
    cornerRadius: 'Corner radius (px)',
    cornerRadiusHelp: 'Rounds cards, panels and map callouts, from 0 to 32. The ad popup gets twice as much.',
  },
  topBar: {
    title: 'Top bar',
    logoHeight: 'Logo height (px)',
    logoHeightHelp: 'Height of the logo in the top bar',
    festivalNameColor: 'Festival name color',
    festivalNameColorHelp: "Next to the logo. Also the title and icons of the notifications screen's bar.",
    backgroundColor: 'Background color',
    backgroundColorHelp: 'Behind the logo and the festival name, and at the top of the notifications screen.',
    backgroundOpacity: 'Background opacity',
  },
  tabBar: {
    title: 'Tab bar',
    selectedColor: 'Selected and glow color',
    unselectedColor: 'Unselected color',
    unselectedColorHelp: 'Icons and labels of the other tabs.',
    backgroundColor: 'Background color',
    backgroundOpacity: 'Background opacity',
  },
  segmentedControl: {
    title: 'Selection tabs',
    help: 'The pill tabs that switch between lineup days, or between maps.',
    textColor: 'Text color',
    selectedTextColor: 'Selected text color',
    backgroundColor: 'Background color',
    backgroundColorHelp: 'The track behind the tabs.',
    backgroundOpacity: 'Background opacity',
    selectedBackgroundColor: 'Selected tab color',
    selectedBackgroundColorHelp: 'The pill behind the selected tab.',
  },
  surface: {
    title: 'Cards and panels',
    backgroundColor: 'Background color',
    backgroundColorHelp: 'Drawn over the festival background: show details, notifications, pages, tickets, news articles, dialogs, menus and map callouts.',
    titleColor: 'Title color',
    titleColorHelp: 'Card titles, and stage names in the timetable.',
    textColor: 'Text color',
    textColorHelp: 'Descriptions and articles. Secondary text uses it at 70%.',
  },
  sheet: {
    title: 'Bottom sheets',
    backgroundColor: 'Background color',
    backgroundColorHelp: 'The panels that slide up from the bottom of the screen.',
  },
  imageText: {
    title: 'Text on photos',
    overlayColor: 'Overlay color',
    overlayColorHelp: 'A gradient under the text of show cards and of news, page and ticket headers, so it stays readable.',
    overlayOpacity: 'Overlay opacity',
    overlayOpacityHelp: 'At the bottom of the photo, between 0.0 (none) and 1.0 (opaque)',
    textColor: 'Text color',
    textColorHelp: 'Titles on the header photos of pages and tickets.',
  },
  showList: {
    title: 'Show list layout',
    help: "How each show appears in the app's lineup. The previews use sample shows with this version's colors and background.",
    tagBackgroundColor: 'Tags and favorite button background color',
    tagTextColor: 'Tags text and favorite icon color',
    layouts: {
      card: {
        title: 'Card',
        description: 'A large artist photo with the name, stage and time on top of it.',
      },
      row: {
        title: 'Row',
        description: 'A compact list: square photo, artist name, time, stage and style tags, and a round favorite button.',
      },
    },
  },
  shows: {
    title: 'Lineup',
    cardHeight: 'Card height (px)',
    cardHeightHelp: 'Height of each show card (min: 40px)',
    detailsHeaderHeight: 'Details header height (px)',
    detailsHeaderHeightHelp: 'Height of the header in details view',
    sectionTitleColor: 'Section title color',
    buttonsTintColor: 'Buttons tint color',
    artistNameColor: 'Artist name color',
    stageNameColor: 'Stage name color',
    dateColor: 'Show date color',
  },
  emptyLineup: {
    title: 'Empty lineup',
    help: "Shown while the lineup isn't released yet or has no shows, with a button to the ticket shop.",
    titleColor: 'Title color',
    buttonTextColor: 'Ticket button text color',
    buttonBackgroundColor: 'Ticket button background color',
  },
  news: {
    title: 'News',
    headerHeight: 'Header height (px)',
    headerHeightHelp: 'Height of the news section header',
    titleColor: 'Title color',
    itemsBackgroundColor: 'Items background color',
    itemsTitleColor: 'Items title color',
    itemsSubtitleColor: 'Items subtitle color',
    itemsDateColor: 'Items date color',
    cellHeight: 'Cell height (px)',
    cellHeightHelp: 'Minimum height for news items (min: 60px)',
    padding: 'Section padding (px)',
    paddingHelp: 'Padding around news items',
    itemsSpacing: 'Items spacing (px)',
    itemsSpacingHelp: 'Space between news items',
    detailsHeaderHeight: 'Details header height (px)',
    detailsHeaderHeightHelp: 'Height of the header in details view',
  },
  pages: {
    title: 'Pages',
    sectionHeight: 'Section height (px)',
    sectionHeightHelp: 'Height of page sections',
    iconHeight: 'Icon height (px)',
    iconHeightHelp: 'Height of page icons',
    rowHeight: 'Row height (px)',
    rowHeightHelp: 'Height of page rows',
    titleColor: 'Title color',
    sectionColor: 'Section color',
    iconTintColor: 'Icon tint color',
    headerHeight: 'Header height (px)',
    headerHeightHelp: 'Height of page headers',
  },
  notifications: {
    title: 'Notifications',
    padding: 'Padding (px)',
    paddingHelp: 'Padding around notifications',
    itemsSpacing: 'Items spacing (px)',
    itemsSpacingHelp: 'Horizontal spacing between items',
    verticalSpacing: 'Items vertical spacing (px)',
    verticalSpacingHelp: 'Vertical spacing between items',
  },
  tickets: {
    title: 'Tickets',
    buttonTextColor: 'Ticket button text color',
    buttonBackgroundColor: 'Ticket button background color',
  },
  map: {
    title: 'Map',
    locationColor: 'Location color',
    locationColorHelp: "The dot showing the user's position on the map, and the direction they face.",
    pinBackgroundColor: 'Pin color',
    pinBackgroundColorHelp: 'Background of the place pins.',
  },
  images: {
    logo: 'Festival logo',
    logoCaption: 'Will be displayed in the app header',
    news: 'News header',
    newsCaption: 'Displayed at the top of news screen',
    tickets: 'Tickets header',
    ticketsCaption: 'Displayed at the top of tickets screen',
  },
};

const fr: typeof en = {
  title: 'Infos & mise en page',
  description: "Le nom de votre festival, l'annonce de son lineup et l'apparence de l'app.",
  sectionsLabel: 'Sections',
  sections: {
    festival: 'Festival',
    theme: 'Thème',
    navigation: 'Navigation',
    surfaces: 'Surfaces',
    screens: 'Écrans',
    images: "Images d'en-tête",
  },
  festival: {
    name: 'Nom du festival',
    nameHelp: "Le nom officiel du festival, affiché dans l'app.",
    internalName: 'Nom interne',
    internalNameHelp: 'Nomme cette version dans le back-office uniquement. Laissez vide pour utiliser le nom du festival.',
  },
  lineup: {
    title: 'Annonce du lineup',
    available: 'Lineup visible par le public',
    releaseDate: "Date d'annonce",
    releaseDatePast: "La date d'annonce est passée — le lineup est visible.",
    help: "Si la case est décochée, l'app masque le lineup jusqu'à la date d'annonce.",
  },
  homepage: {
    title: 'Accueil',
    enabled: 'Ouvrir l’app sur l’accueil',
    help: 'Ajoute un accueil avec les shows en cours, les recos de la team, les actus et des raccourcis utiles. La programmation reste accessible dans son onglet.',
    startDate: 'Début du festival',
    endDate: 'Fin du festival',
    datesHelp: 'Facultatif : sinon, les dates du premier et du dernier show sont utilisées. Incluez les derniers sets après minuit. Ces dates déterminent l’affichage avant, pendant et après le festival.',
    beforeMessage: 'Message avant le festival',
    afterMessage: 'Message après le festival',
    messagesHelp: 'Laissez vide pour utiliser les messages de bienvenue et de remerciement par défaut de l’app.',
  },
  reminders: {
    title: 'Rappels de show',
    minutes: 'Rappel avant un show (minutes)',
    placeholder: (minutes: number) => `${minutes} (par défaut)`,
    help: (minutes: number) =>
      `Le délai entre le rappel de l'app et le début d'un show suivi. Laissez vide pour utiliser la valeur par défaut de l'app (${minutes} min). Les festivaliers peuvent toujours la modifier dans l'app.`,
  },
  opacityHelp: 'Valeur entre 0 (transparent) et 1 (opaque)',
  brand: {
    title: 'Couleurs du festival',
    accentColor: "Couleur d'accent",
    accentColorHelp: 'Boutons, interrupteurs, chargements, badges, filtres actifs et planning.',
    accentColorAutomatic:
      "Boutons, interrupteurs, chargements, badges, filtres actifs et planning. Automatique\u00a0: suit la couleur de sélection de la barre d'onglets.",
    onAccentColor: "Texte sur l'accent",
    onAccentColorHelp: "Textes et icônes posés sur la couleur d'accent, comme le libellé des boutons.",
    onAccentColorAutomatic:
      "Textes et icônes posés sur la couleur d'accent, comme le libellé des boutons. Automatique\u00a0: noir ou blanc, selon ce qui se lit le mieux.",
    backToAutomatic: 'Revenir en automatique',
    linkColor: 'Couleur des liens',
    linkColorHelp: 'Les liens des actus, des pages et de la billetterie.',
    sampleButton: 'Bouton',
    sampleLink: 'Lien',
  },
  text: {
    title: "Texte sur l'arrière-plan",
    help: "Le texte posé directement sur l'arrière-plan du festival : titre de l'écran de lancement, séparateurs de jours et écrans vides.",
    titleColor: 'Couleur des titres',
    captionColor: 'Couleur des légendes',
    bodyColor: 'Couleur du texte courant',
  },
  background: {
    title: 'Arrière-plan',
    type: "Type d'arrière-plan",
    types: {
      color: 'Couleur unie',
      gradient: 'Dégradé',
      image: 'Image',
    },
    primaryColor: 'Couleur principale',
    secondaryColor: 'Couleur secondaire',
    image: "Image d'arrière-plan",
    imageOpacity: "Opacité de l'image",
    baseColor: "Couleur sous l'image",
    baseColorHelp: "Apparaît à travers l'image quand son opacité baisse.",
  },
  shapes: {
    title: 'Formes',
    cornerRadius: 'Arrondi des angles (px)',
    cornerRadiusHelp: 'Arrondit les cartes, les panneaux et les bulles du plan, de 0 à 32. La pop-up de publicité en prend le double.',
  },
  topBar: {
    title: 'Barre du haut',
    logoHeight: 'Hauteur du logo (px)',
    logoHeightHelp: 'Hauteur du logo dans la barre du haut',
    festivalNameColor: 'Couleur du nom du festival',
    festivalNameColorHelp: "À côté du logo. Colore aussi le titre et les icônes de la barre de l'écran des notifications.",
    backgroundColor: "Couleur d'arrière-plan",
    backgroundColorHelp: "Derrière le logo et le nom du festival, et en haut de l'écran des notifications.",
    backgroundOpacity: "Opacité de l'arrière-plan",
  },
  tabBar: {
    title: "Barre d'onglets",
    selectedColor: 'Couleur de sélection et du halo',
    unselectedColor: 'Couleur des onglets non sélectionnés',
    unselectedColorHelp: 'Icônes et libellés des autres onglets.',
    backgroundColor: "Couleur d'arrière-plan",
    backgroundOpacity: "Opacité de l'arrière-plan",
  },
  segmentedControl: {
    title: 'Onglets de sélection',
    help: "Les onglets en pastille qui passent d'un jour du lineup à l'autre, ou d'un plan à l'autre.",
    textColor: 'Couleur du texte',
    selectedTextColor: 'Couleur du texte sélectionné',
    backgroundColor: "Couleur d'arrière-plan",
    backgroundColorHelp: 'Le rail derrière les onglets.',
    backgroundOpacity: "Opacité de l'arrière-plan",
    selectedBackgroundColor: "Couleur de l'onglet sélectionné",
    selectedBackgroundColorHelp: "La pastille derrière l'onglet sélectionné.",
  },
  surface: {
    title: 'Cartes et panneaux',
    backgroundColor: "Couleur d'arrière-plan",
    backgroundColorHelp: "Posés sur l'arrière-plan du festival : détail des shows, notifications, pages, billetterie, articles d'actus, fenêtres, menus et bulles du plan.",
    titleColor: 'Couleur des titres',
    titleColorHelp: 'Titres des cartes, et noms des scènes dans le planning.',
    textColor: 'Couleur du texte',
    textColorHelp: 'Descriptions et articles. Le texte secondaire la reprend à 70 %.',
  },
  sheet: {
    title: 'Panneaux du bas',
    backgroundColor: "Couleur d'arrière-plan",
    backgroundColorHelp: "Les panneaux qui montent du bas de l'écran.",
  },
  imageText: {
    title: 'Texte sur les photos',
    overlayColor: 'Couleur du voile',
    overlayColorHelp: "Un voile en dégradé sous le texte des cartes de show et des en-têtes d'actus, de pages et de billetterie, pour qu'il reste lisible.",
    overlayOpacity: 'Opacité du voile',
    overlayOpacityHelp: 'En bas de la photo, entre 0 (aucun voile) et 1 (opaque)',
    textColor: 'Couleur du texte',
    textColorHelp: "Titres sur les photos d'en-tête des pages et de la billetterie.",
  },
  showList: {
    title: 'Mise en page de la liste des shows',
    help: "L'apparence de chaque show dans le lineup de l'app. Les aperçus utilisent des shows d'exemple avec les couleurs et l'arrière-plan de cette version.",
    tagBackgroundColor: "Couleur d'arrière-plan des étiquettes et du bouton favori",
    tagTextColor: "Couleur du texte des étiquettes et de l'icône favori",
    layouts: {
      card: {
        title: 'Vignette',
        description: "Une grande photo de l'artiste, avec le nom, la scène et l'horaire par-dessus.",
      },
      row: {
        title: 'Liste',
        description: "Une liste compacte : photo carrée, nom de l'artiste, horaire, scène et style en étiquettes, et un bouton favori rond.",
      },
    },
  },
  shows: {
    title: 'Lineup',
    cardHeight: 'Hauteur des cartes (px)',
    cardHeightHelp: 'Hauteur de chaque carte de show (min : 40 px)',
    detailsHeaderHeight: "Hauteur de l'en-tête du détail (px)",
    detailsHeaderHeightHelp: "Hauteur de l'en-tête dans la vue détaillée",
    sectionTitleColor: 'Couleur des titres de section',
    buttonsTintColor: 'Teinte des boutons',
    artistNameColor: "Couleur du nom de l'artiste",
    stageNameColor: 'Couleur du nom de la scène',
    dateColor: 'Couleur de la date du show',
  },
  emptyLineup: {
    title: 'Lineup vide',
    help: "Affiché tant que le lineup n'est pas annoncé ou ne contient aucun show, avec un bouton vers la billetterie.",
    titleColor: 'Couleur du titre',
    buttonTextColor: 'Couleur du texte du bouton billetterie',
    buttonBackgroundColor: "Couleur d'arrière-plan du bouton billetterie",
  },
  news: {
    title: 'Actus',
    headerHeight: "Hauteur de l'en-tête (px)",
    headerHeightHelp: "Hauteur de l'en-tête de la section des actus",
    titleColor: 'Couleur du titre',
    itemsBackgroundColor: "Couleur d'arrière-plan des actus",
    itemsTitleColor: 'Couleur du titre des actus',
    itemsSubtitleColor: 'Couleur du sous-titre des actus',
    itemsDateColor: 'Couleur de la date des actus',
    cellHeight: 'Hauteur des cellules (px)',
    cellHeightHelp: 'Hauteur minimale des actus (min : 60 px)',
    padding: 'Marge intérieure de la section (px)',
    paddingHelp: 'Marge autour des actus',
    itemsSpacing: 'Espacement des actus (px)',
    itemsSpacingHelp: 'Espace entre les actus',
    detailsHeaderHeight: "Hauteur de l'en-tête du détail (px)",
    detailsHeaderHeightHelp: "Hauteur de l'en-tête dans la vue détaillée",
  },
  pages: {
    title: 'Pages',
    sectionHeight: 'Hauteur des sections (px)',
    sectionHeightHelp: 'Hauteur des sections de pages',
    iconHeight: 'Hauteur des icônes (px)',
    iconHeightHelp: 'Hauteur des icônes de pages',
    rowHeight: 'Hauteur des lignes (px)',
    rowHeightHelp: 'Hauteur des lignes de pages',
    titleColor: 'Couleur des titres',
    sectionColor: 'Couleur des sections',
    iconTintColor: 'Teinte des icônes',
    headerHeight: "Hauteur de l'en-tête (px)",
    headerHeightHelp: "Hauteur de l'en-tête des pages",
  },
  notifications: {
    title: 'Notifications',
    padding: 'Marge intérieure (px)',
    paddingHelp: 'Marge autour des notifications',
    itemsSpacing: 'Espacement des notifications (px)',
    itemsSpacingHelp: 'Espacement horizontal entre les notifications',
    verticalSpacing: 'Espacement vertical des notifications (px)',
    verticalSpacingHelp: 'Espacement vertical entre les notifications',
  },
  tickets: {
    title: 'Billetterie',
    buttonTextColor: 'Couleur du texte du bouton billetterie',
    buttonBackgroundColor: "Couleur d'arrière-plan du bouton billetterie",
  },
  map: {
    title: 'Plan',
    locationColor: 'Couleur de la position',
    locationColorHelp: 'Le point qui montre la position du festivalier sur le plan, et la direction vers laquelle il regarde.',
    pinBackgroundColor: 'Couleur des épingles',
    pinBackgroundColorHelp: 'Le fond des épingles des lieux.',
  },
  images: {
    logo: 'Logo du festival',
    logoCaption: "Affiché dans l'en-tête de l'app",
    news: 'En-tête des actus',
    newsCaption: "Affiché en haut de l'écran des actus",
    tickets: 'En-tête de la billetterie',
    ticketsCaption: "Affiché en haut de l'écran de la billetterie",
  },
};

const messages = { en, fr };
export default messages;
